import { useEffect, useRef } from 'react';

import { lottieAnimations } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import { TestBoosterBanner } from './TestBoosterBanner';
import { boosterPageSx } from '../satActBooster.styles';

const STRINGS = appStrings.modules.satActBooster.report;

// Square, so the art fills the box at every width instead of letterboxing inside a fixed height.
const ANIMATION_SIZE = 'min(600px, 100%)';

type BoosterReportLoadingProps = Readonly<{
  /** The report has landed; hold the screen a beat with the motion stopped. */
  ready?: boolean;
}>;

export const BoosterReportLoading = ({ ready = false }: BoosterReportLoadingProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  // Freezing beats `loop={false}`, which would run out the remaining ~3s cycle first.
  useEffect(() => {
    if (ready) lottieRef.current?.pause();
  }, [ready]);

  return (
    <Box sx={boosterPageSx}>
      <TestBoosterBanner />
      <Box sx={containerSx}>
        <Box sx={animationBoxSx}>
          <Lottie
            lottieRef={lottieRef}
            animationData={lottieAnimations.seoAnalytics}
            loop
            style={animationStyle}
          />
        </Box>
        <Typography variant="display-xs-semibold" sx={titleSx}>
          {ready ? STRINGS.ready : STRINGS.calculating}
        </Typography>
        {!ready && (
          <Typography variant="text-md-regular" sx={hintSx}>
            {STRINGS.calculatingHint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: `-${backrs.spacing['spacing-4xl']}`,
});

const animationBoxSx: SxProps<Theme> = {
  width: ANIMATION_SIZE,
  aspectRatio: '1 / 1',
  // The composition's own padding scales with the render size, so the pull has to scale with it.
  marginBottom: `calc(${ANIMATION_SIZE} * -0.20)`,
  marginTop: `calc(${ANIMATION_SIZE} * -0.165)`,
};

const animationStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
};

const titleSx: SxProps<Theme> = { textAlign: 'center' };

const hintSx: SxProps<Theme> = ({ backrs }) => ({
  textAlign: 'center',
  color: backrs.colors['Colors/Text/text-tertiary'],
  marginTop: backrs.spacing['spacing-md'],
});
